// Wind-speed colour scale, ported from sites/my-fdi (constants/colors.ts +
// utils/color.ts). Maps wind speed in knots to an RGBA string for the
// particle trails.

// Wind speed colour gradient stops: [normalizedPosition, [r, g, b]]
export const WIND_COLOR_STOPS: [number, [number, number, number]][] = [
  [0.00, [26,  74,  138]],
  [0.10, [42,  138, 191]],
  [0.22, [58,  184, 122]],
  [0.36, [157, 200, 71]],
  [0.50, [240, 216, 32]],
  [0.64, [240, 152, 32]],
  [0.78, [208, 64,  32]],
  [1.00, [144, 32,  96]],
];

// Satellite variant. The default ramp opens on dark blue (26,74,138),
// which is close to invisible over dark green and brown imagery — and low
// wind is the common case, so most of the field disappeared. This keeps the
// same progression but starts pale and cool-white, so the low end reads as
// bright strokes rather than dark ones. The hot end is already legible and
// is left alone so the two ramps agree about what 'fast' looks like.
export const WIND_COLOR_STOPS_SATELLITE: [number, [number, number, number]][] = [
  [0.00, [232, 245, 255]],
  [0.10, [175, 224, 255]],
  [0.22, [140, 235, 200]],
  [0.36, [196, 231, 120]],
  [0.50, [250, 232, 90]],
  [0.64, [250, 176, 70]],
  [0.78, [235, 100, 65]],
  [1.00, [214, 96,  158]],
];

export type WindColorVariant = 'default' | 'satellite';

// Max knots for the wind colour scale.
export const WIND_MAX_KT = 50;

/** Map wind speed in knots to an RGBA colour string. */
export function ktColor(
  kt: number,
  alpha: number = 1,
  variant: WindColorVariant = 'default',
): string {
  const stops = variant === 'satellite' ? WIND_COLOR_STOPS_SATELLITE : WIND_COLOR_STOPS;
  const t = Math.min(kt / WIND_MAX_KT, 1);
  let lo = stops[0]!;
  let hi = stops[stops.length - 1]!;
  for (let i = 0; i < stops.length - 1; i++) {
    const a = stops[i]!;
    const b = stops[i + 1]!;
    if (t >= a[0] && t <= b[0]) {
      lo = a;
      hi = b;
      break;
    }
  }
  const f = lo[0] === hi[0] ? 0 : (t - lo[0]) / (hi[0] - lo[0]);
  const [lr, lg, lb] = lo[1];
  const [hr, hg, hb] = hi[1];
  const r = Math.round(lr + (hr - lr) * f);
  const g = Math.round(lg + (hg - lg) * f);
  const b = Math.round(lb + (hb - lb) * f);
  return `rgba(${r},${g},${b},${alpha})`;
}
